import { type MutableRefObject, useEffect } from "react";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Select, { SelectRenderer } from "react-dropdown-select";
import { Option } from "src/components/ui/TGSelect";
import PeoplePicker from "src/components/ui/form/peoplePicker";
import { useAppSelector } from "src/hooks/redux";
import { selectCurrentUser } from "src/modules/users/selector";
import { selectSearchPeople } from "src/types/form";
import { twMerge } from "tailwind-merge";

export type CollectionOption = {
  id: string;
  name: string;
  value: any;
};

type ChangeResponsibleReviewModalProps = {
  collections: CollectionOption[];
  selectedCollections: CollectionOption[];
  currentResponsible: Option[];
  newResponsible: Option[];
  onChangeCollections: (collections: CollectionOption[]) => void;
  onChangeCurrentResponsible: (responsible: Option[]) => void;
  onChangeNewResponsible: (responsible: Option[]) => void;
  hasInitializedCurrentResponsible: MutableRefObject<boolean>;
  onCancel: () => void;
  onClose: () => void;
  onSubmit: () => void;
};

const currentResponsibleField = {
  placeholder: "Введите имя сотрудника",
  type: "peoplePicker",
  require: false,
  title: "Текущий ответственный",
  width: "w-full",
  key: "currentResponsible",
  single: true,
};

const newResponsibleField = {
  placeholder: "Введите имя сотрудника",
  type: "peoplePicker",
  require: false,
  title: "Новый ответственный",
  width: "w-full",
  key: "newResponsible",
  single: true,
};

const toPeoplePickerValue = (responsible: Option[]): selectSearchPeople[] =>
  responsible
    .map((option) => {
      const user = option.value || {};
      const email = String(user.Email || user.email || option.id || "").toLowerCase().trim();

      if (!email) {
        return null;
      }

      return {
        id: Number(user.Id || user.ID || option.id) || 0,
        value: email,
        label: option.name || user.DisplayName || user.Title || user.Name || email,
        avatar: user.PictureUrl || user.avatar || "",
      };
    })
    .filter(Boolean) as selectSearchPeople[];

const toResponsibleOption = (people: selectSearchPeople[]): Option[] =>
  people.map((person) => ({
    id: String(person.id),
    name: person.label,
    value: {
      Id: person.id,
      ID: person.id,
      Email: person.value,
      email: person.value,
      DisplayName: person.label,
      Title: person.label,
      Name: person.label,
      PictureUrl: person.avatar,
    },
  }));

const toResponsibleOptionFromCurrentUser = (currentUser: any): Option => ({
  id: String(currentUser.id),
  name: currentUser.displayName,
  value: {
    Id: currentUser.id,
    ID: currentUser.id,
    Email: currentUser.email,
    email: currentUser.email,
    DisplayName: currentUser.displayName,
    Title: currentUser.displayName,
    Name: currentUser.displayName,
    PictureUrl: currentUser.avatar,
  },
});

function ChangeResponsibleReviewModal({
  collections,
  selectedCollections,
  currentResponsible,
  newResponsible,
  onChangeCollections,
  onChangeCurrentResponsible,
  onChangeNewResponsible,
  hasInitializedCurrentResponsible,
  onCancel,
  onClose,
  onSubmit,
}: ChangeResponsibleReviewModalProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const isSubmitDisabled = selectedCollections.length === 0;

  useEffect(() => {
    if (
      hasInitializedCurrentResponsible.current ||
      currentResponsible.length > 0 ||
      !currentUser?.email
    ) {
      return;
    }

    onChangeCurrentResponsible([toResponsibleOptionFromCurrentUser(currentUser)]);
    hasInitializedCurrentResponsible.current = true;
  }, [
    currentResponsible.length,
    currentUser,
    onChangeCurrentResponsible,
    hasInitializedCurrentResponsible,
  ]);

  const handleResponsibleChange = (
    value: selectSearchPeople[] | null,
    isCurrentResponsible: boolean
  ) => {
    const onChangeResponsible = isCurrentResponsible
      ? onChangeCurrentResponsible
      : onChangeNewResponsible;

    onChangeResponsible(
      !value || value.length === 0 ? [] : toResponsibleOption(value)
    );
  };

  const contentRenderer = ({ state }: SelectRenderer<CollectionOption>) => {
    if (!state.values.length) {
      return <span className="text-[16px] leading-[22px] text-[#9CA3AF]">Выберите коллекцию</span>;
    }

    return (
      <div className="flex flex-wrap gap-1.5 py-1">
        {state.values.map((collection) => (
          <span key={collection.id} className="rounded bg-[#F3F4F6] px-2 py-1 text-[16px] leading-[22px] text-[#111928]">
            {collection.name}
          </span>
        ))}
      </div>
    );
  };

  const dropdownHandleRenderer = ({ state }: SelectRenderer<CollectionOption>) => (
    <ChevronDownIcon className={twMerge("h-5 w-5 text-[#9CA3AF]", state.dropdown && "rotate-180 transform")} />
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20">
      <div className="relative flex w-[600px] flex-col rounded-lg bg-white shadow-xl">
        <button
          type="button"
          className="absolute left-[calc(100%+16px)] top-0 flex items-center justify-center"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <XMarkIcon className="h-[36px] w-[36px] text-white" strokeWidth={1.5} />
        </button>

        <div className="px-10 pt-[45px] pb-10">
          <h2 className="mb-[43px] text-[24px] leading-[30px] font-bold text-[#111928]">
            Изменение ответственного за пересмотр
          </h2>

          <div className="space-y-[23px]">
            <PeoplePicker
              key={`current-${currentResponsible.map((option) => option.id).join(",")}`}
              type="change_responsible"
              dataField={currentResponsibleField}
              value={toPeoplePickerValue(currentResponsible)}
              onChange={(value) => handleResponsibleChange(value, true)}
            />
            <PeoplePicker
              key={`new-${newResponsible.map((option) => option.id).join(",")}`}
              type="change_responsible"
              dataField={newResponsibleField}
              value={toPeoplePickerValue(newResponsible)}
              onChange={(value) => handleResponsibleChange(value, false)}
            />

            <label className="block custom-selects">
              <span className="block mb-2.5 text-[16px] leading-[22px] font-normal text-[#111928]">Коллекция</span>
              <Select<CollectionOption>
                multi
                options={collections}
                values={selectedCollections}
                onChange={onChangeCollections}
                labelField="name"
                valueField="id"
                searchable={false}
                contentRenderer={contentRenderer}
                dropdownHandleRenderer={dropdownHandleRenderer}
                noDataLabel="Нет данных"
                className="!min-h-[53px] !rounded-md !border-[#DFE4EA] !px-5 !text-[16px]"
              />
            </label>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-between border-t border-[#DFE4EA] p-10">
          <button
            type="button"
            className="h-[54px] min-w-[122px] rounded-md border border-[#DFE4EA] bg-white px-6 text-[16px] leading-[22px] font-medium text-[#012169] shadow-sm"
            onClick={onCancel}
          >
            Отмена
          </button>
          <button
            type="button"
            className={twMerge(
              "h-[54px] min-w-[139px] rounded-md px-6 text-[16px] leading-[22px] font-medium",
              isSubmitDisabled
                ? "cursor-not-allowed bg-[#E5E7EB] text-[#8899A8]"
                : "bg-[#012169] text-white"
            )}
            disabled={isSubmitDisabled}
            onClick={onSubmit}
          >
            Изменить
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChangeResponsibleReviewModal;
